import React, { useState } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Group,
  Input,
  LoadingOverlay,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import { IconAlertCircle } from '@tabler/icons-react';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import Drawer from 'components/Drawer';
import { useAuth } from 'context/auth-context';
import useCreateDocument from 'hooks/document/useCreateDocument';
import useDocument from 'hooks/document/useDocument';
import useUpdateDocument from 'hooks/document/useUpdateDocument';
import useGym from 'hooks/gym/useGym';
import notificationHandler from 'utils/notifications';
import { Image } from 'utils/tip-tap-image-extension';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  documentId: string;
  isTemplate: boolean;
  type: 'contract' | 'waiver';
  description: string;
  contents: string;
  generateWaiverOnAthleteCreate: boolean;
  requireSignatureForAttendance: boolean;
  packages: string[];
}

const templateVariables: { [key: string]: string } = {
  'Athlete Name': 'ATHLETE_NAME',
  'Athlete Address': 'ATHLETE_ADDRESS',
  'Athlete Email Address': 'ATHLETE_EMAIL_ADDRESS',
  'Athlete Phone Number': 'ATHLETE_PHONE_NUMBER',
  'Athlete Date Of Birth': 'ATHLETE_DATE_OF_BIRTH',
  'Athlete Gender': 'ATHLETE_GENDER',
  'Gym Name': 'GYM_NAME',
  'Gym Address': 'GYM_ADDRESS',
  'Gym Logo': 'GYM_LOGO',
  [`Today's Date`]: `TODAYS_DATE`,
};

const DrawerWaiverTemplateForm = ({
  formId,
  opened,
  documentId,
  closeHandler,
  permission = true,
}: {
  formId: string;
  opened: boolean;
  documentId?: string | null;
  closeHandler: () => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();

  const form = useForm<FormValues>({
    initialValues: {
      documentId: uuidv4(),
      isTemplate: true,
      type: 'waiver',
      description: '',
      contents: '',
      generateWaiverOnAthleteCreate: false,
      requireSignatureForAttendance: false,
      packages: [],
    },

    validate: {
      description: value =>
        !value || value.length > 255 ? 'Description must be between 1-255 characters.' : null,
      contents: value => (!value || value === '<p></p>' ? 'This field may not be blank.' : null),
    },
  });

  const {
    data: documentData,
    isLoading: isDocumentLoading,
    isFetching: isDocumentFetching,
    isSuccess: isDocumentSuccess,
  } = useDocument(documentId || '');
  const { data: gymData, isLoading: isGymLoading } = useGym(session?.gym?.gymId);

  const createMutation = useCreateDocument();
  const updateMutation = useUpdateDocument();

  const { successNotification, errorNotification } = notificationHandler();

  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline as AnyExtension,
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'document-gym-logo',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: form.getInputProps('contents').value,
    onUpdate: ({ editor: _editor }) => form.getInputProps('contents').onChange(_editor.getHTML()),
    editable: permission,
  });

  const [logoWidth, setLogoWidth] = useState(200);
  const [logoHeight, setLogoHeight] = useState(100);

  const updateImgAttributes = (attributeName: 'width' | 'height', attributeValue: number): void => {
    // Create a temporary DOM element to parse the HTML string
    const documentToParse = document.createElement('div');
    documentToParse.innerHTML = editor?.getHTML() || '';

    // Get all img elements in the temporary element
    const imgElements = documentToParse.querySelectorAll('img');

    // Loop through each img element and update the specified attribute
    imgElements.forEach(imgElement => {
      imgElement.setAttribute(attributeName, String(attributeValue));
    });

    // Get the updated HTML string
    const updatedHtmlText = documentToParse.innerHTML;

    // Update the editor and form with the modified HTML
    editor?.commands.setContent(updatedHtmlText);
    form.getInputProps('contents').onChange(updatedHtmlText);
  };

  const handleLogoWidthChange = (width: number): void => {
    setLogoWidth(width);
    updateImgAttributes('width', width);
  };

  const handleLogoHeightChange = (height: number): void => {
    setLogoHeight(height);
    updateImgAttributes('height', height);
  };

  const handleTemplateVariableClick = (variable: string): void => {
    const templateToInsert = templateVariables[variable];

    if (templateToInsert === 'GYM_LOGO') {
      if (!!gymData?.data.profilePicture) {
        editor
          ?.chain()
          .setImage({
            src: gymData?.data.profilePicture,
            width: String(logoWidth),
            height: String(logoHeight),
          })
          .focus()
          .run();
      } else {
        errorNotification("Gym Logo doesn't exist", 'This action is disabled until you upload gym logo.');
      }
    } else {
      editor?.chain().insertContent(`${templateToInsert} `).focus().run();
    }
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
    editor?.commands.setContent('');
  };

  const handleSubmit = (values: FormValues): void => {
    if (documentId) {
      updateMutation.mutate(
        { ...values, athlete: null },
        {
          onSuccess: response => {
            successNotification(
              'Document template update success',
              `${response.data.description} successfully updated.`,
            );
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification('Document template update failed', error.response?.data.message);
          },
        },
      );
    } else if (session?.staff) {
      createMutation.mutate(
        {
          ...values,
          documentId: uuidv4(),
          athlete: null,
        },
        {
          onSuccess: response => {
            successNotification(
              'Document template creation success',
              `${response.data.description} successfully created.`,
            );
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification('Document template creation failed', error.response?.data.message);
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (documentData?.data && isDocumentSuccess && documentId && opened) {
      form.setValues(documentData.data);
      editor?.commands.setContent(documentData.data.contents);

      if (documentData.data.contents.includes('img')) {
        // Create a temporary DOM element to parse the HTML string
        const documentToParse = document.createElement('div');
        documentToParse.innerHTML = documentData.data.contents;
        // Get all img elements in the temporary element
        const imgElement = documentToParse.querySelectorAll('img')[0];
        const initialWidthStr = imgElement.getAttribute('width');
        const initialHeightStr = imgElement.getAttribute('height');
        if (!!initialWidthStr) {
          setLogoWidth(Number(initialWidthStr));
        }
        if (!!initialHeightStr) {
          setLogoHeight(Number(initialHeightStr));
        }
      }
    }
  }, [documentData?.data, documentId, opened]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size={900}
      title={
        <Title order={3}>{documentId ? documentData?.data?.description : 'Create Waiver Template'}</Title>
      }
      closeOnClickOutside
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            disabled={!permission || isDocumentLoading || isDocumentFetching || isGymLoading}
            loading={createMutation.isLoading || updateMutation.isLoading}
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={isDocumentLoading || isDocumentFetching || isGymLoading} />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='md'>
          <TextInput label='Description' {...form.getInputProps('description')} disabled={!permission} />

          <Stack spacing='xs'>
            <Checkbox
              disabled={!permission}
              label={
                <Text fs='normal' size='sm' color='gray.9'>
                  Automatically generate waiver on athlete creation
                </Text>
              }
              checked={form.values.generateWaiverOnAthleteCreate}
              {...form.getInputProps('generateWaiverOnAthleteCreate')}
            />
            <Checkbox
              disabled={!permission}
              label={
                <Text fs='normal' size='sm' color='gray.9'>
                  Require signed waiver for athlete reservations
                </Text>
              }
              checked={form.values.requireSignatureForAttendance}
              {...form.getInputProps('requireSignatureForAttendance')}
            />
          </Stack>

          <Divider mx='-xl' />

          <Stack spacing='xs'>
            <Group spacing='xs'>
              {Object.keys(templateVariables).map(variable => (
                <Button
                  key={variable}
                  radius='sm'
                  size='xs'
                  compact
                  onClick={() => handleTemplateVariableClick(variable)}
                  disabled={!permission}
                >
                  {variable}
                </Button>
              ))}
            </Group>
          </Stack>

          {form.errors.contents && (
            <Alert icon={<IconAlertCircle size={theme.fontSizes.lg} />} color='red'>
              {form.errors.contents}
            </Alert>
          )}

          <RichTextEditor editor={editor}>
            <RichTextEditor.Toolbar sticky display={permission ? 'default' : 'none'} stickyOffset={-24}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 />
                <RichTextEditor.H2 />
                <RichTextEditor.H3 />
                <RichTextEditor.H4 />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.AlignLeft />
                <RichTextEditor.AlignCenter />
                <RichTextEditor.AlignJustify />
                <RichTextEditor.AlignRight />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Hr />
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup sx={{ gap: theme.spacing.xs }}>
                <Group noWrap spacing='xs'>
                  <Input.Label size='xs'>Logo width:</Input.Label>
                  <NumberInput
                    value={logoWidth}
                    onChange={handleLogoWidthChange}
                    step={10}
                    min={0}
                    w={100}
                    size='xs'
                  />
                </Group>
                <Group noWrap spacing='xs'>
                  <Input.Label size='xs'>Logo height:</Input.Label>
                  <NumberInput
                    value={logoHeight}
                    onChange={handleLogoHeightChange}
                    step={10}
                    min={0}
                    size='xs'
                    w={100}
                  />
                </Group>
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>
            <RichTextEditor.Content />
          </RichTextEditor>
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerWaiverTemplateForm;
